import React from "react";
import ReactDOM from "react-dom/client";
import { createHashRouter, Navigate, RouterProvider } from "react-router-dom";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType, AccountInfo, InteractionType } from "@azure/msal-browser";
import { msalConfig, useLogin } from "./authConfig";

import "./index.css";

import Layout from "./pages/layout/Layout";
import Chat from "./pages/chat/Chat";
import Admin from "./pages/admin/Admin";
import Info from "./pages/info/Info";
import LandingPage from "./pages/landingPage/LandingPage";
import { ProtectedRoute } from "./components/ProtectedRoute/ProtectedRoute";
import {ADMIN_AND_IT_ROLES, ALL_VALID_ROLES} from "./constants";
import NoAccess from "./pages/noAccess/NoAccess";

let layout;
if (useLogin) {
    const msalInstance = new PublicClientApplication(msalConfig);

    // with MSAL browser 4.x --> uninitialized_public_client_application
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/errors.md#uninitialized_public_client_application
    // Fix: Initialize the MSAL instance before using it
    msalInstance.initialize().then(() => {
        // Default to using the first account if no account is active on page load
        if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
            // Account selection logic is app dependent. Adjust as needed for different use cases.
            msalInstance.setActiveAccount(msalInstance.getActiveAccount());
        }

        // Listen for sign-in event and set active account
        msalInstance.addEventCallback(event => {
            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                const account = event.payload as AccountInfo;
                msalInstance.setActiveAccount(account);
            }
        });
    });

    layout = (
        <MsalProvider instance={msalInstance}>
            <MsalAuthenticationTemplate interactionType={InteractionType.Redirect}>
                <Layout />
            </MsalAuthenticationTemplate>
        </MsalProvider>
    );
} else {
    layout = <Layout />;
}

const router = createHashRouter([
    {
        path: "/",
        element: layout,
        children: [
            {
                path: "/unauthorized",
                element: <NoAccess />
            },
            {
                element: <ProtectedRoute roles={ADMIN_AND_IT_ROLES} redirectPath="/unauthorized" />,
                children: [
                    {
                        path: "admin",
                        element: <Admin />
                    }
                ]
            },
            {
                element: <ProtectedRoute roles={ALL_VALID_ROLES} redirectPath="/unauthorized" />,

                children: [
                    {
                        path: "chat",
                        element: <Chat />
                    },
                    {
                        path: "/",
                        index: true,
                        element: <LandingPage />
                    },
                    {
                        path: "info",
                        element: <Info />
                    }
                ]
            },

            {
                path: "*",
                element: <Navigate to="/" />
            }
        ]
    }
]);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <React.StrictMode>
        <RouterProvider router={router} />
    </React.StrictMode>
);
